// src/components/LearningModules.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Dialog,
  Paper,
  DialogTitle,
  DialogContent,
  DialogActions,
  CardMedia,
  Chip,
  LinearProgress,
  Stack,
  Avatar,
  IconButton,
  Divider,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import { PlayArrow, CheckCircle, HourglassEmpty, Replay, Quiz as QuizIcon, Close } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from '../i18n/i18n';
import API_ENDPOINTS from '../config/apiConfig';
import FixedSnackbar from './FixedSnackbar';

function LearningModules({ role }) {
  const { t } = useTranslation();
  const [learningModules, setLearningModules] = useState([]);
  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [newModuleQuestions, setNewModuleQuestions] = useState([]);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [question, setQuestion] = useState('');
  const [answers, setAnswers] = useState(['', '', '', '']);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoFileName, setVideoFileName] = useState('');
  const [showAddModuleModal, setShowAddModuleModal] = useState(false);
  const [currentModule, setCurrentModule] = useState(null);
  const [videoSrc, setVideoSrc] = useState('');
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoRefreshAttempts, setVideoRefreshAttempts] = useState(0);
  const sasCache = useRef({}); // { [videoid]: { sas_url, expires_at } }
  const thumbnailSasCache = useRef({}); // { [videoid]: sas_url } for custom module thumbnails
  const [resolvedThumbnailUrls, setResolvedThumbnailUrls] = useState({}); // { [videoid]: sas_url }
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [lastSelectionCorrect, setLastSelectionCorrect] = useState(null);
  const [lastSelectionExplanation, setLastSelectionExplanation] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // local UI state
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [quizResults, setQuizResults] = useState({ passed: false, score: 0, minPassingScore: 0 });

  const fetchModules = async () => {
    try {
      // Fetch user-specific learning modules
      const userModulesResponse = await fetch(API_ENDPOINTS.BASEURL + '/api/user/learning_modules', { credentials: 'include' });
      const userModulesData = await userModulesResponse.json();

      // Fetch all learning modules
      const allModulesResponse = await fetch(API_ENDPOINTS.BASEURL + '/api/learning_modules', { credentials: 'include' });
      const allModulesData = await allModulesResponse.json();

      // Correlate user modules with full module details
      // Keys in userModulesData are now videoid values (the stable identifier)
      const mergedModules = Object.entries(userModulesData).map(([vid, userModule]) => {
        const fullModule = allModulesData.find(module => module.videoid === vid) || {};
        return { ...fullModule, ...userModule, videoid: vid };
      });

      // sort by status -> assigned first
      mergedModules.sort((a, b) => (a.state === 'assigned' && b.state !== 'assigned' ? -1 : 0));

      setLearningModules(mergedModules);

      // Resolve thumbnail SAS URLs for custom modules in the background
      const customModules = mergedModules.filter(m => m.source === 'custom' && m.thumbnailURL && m.videoid);
      if (customModules.length > 0) {
        const urlMap = {};
        await Promise.all(customModules.map(async (m) => {
          try {
            const url = await getThumbnailUrl(m);
            if (url) {
              urlMap[String(m.videoid)] = url;
              thumbnailSasCache.current[String(m.videoid)] = url;
            }
          } catch {}
        }));
        if (Object.keys(urlMap).length > 0) {
          setResolvedThumbnailUrls(prev => {
            const updated = { ...prev, ...urlMap };
            try {
              sessionStorage.setItem('learning_thumbnail_sas_cache', JSON.stringify(updated));
            } catch (e) {
              console.warn('Failed to save thumbnail SAS cache to sessionStorage', e);
            }
            return updated;
          });
        }
      }
    } catch (error) {
      console.error('Error fetching learning modules:', error);
    }
  };

  useEffect(() => {
    fetchModules();
    // Hydrate SAS cache from sessionStorage so SAS survives page reloads
    try {
      const stored = sessionStorage.getItem('learning_sas_cache');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          sasCache.current = parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to load SAS cache from sessionStorage', e);
    }
    // Hydrate thumbnail SAS cache from sessionStorage
    try {
      const storedThumbnails = sessionStorage.getItem('learning_thumbnail_sas_cache');
      if (storedThumbnails) {
        const parsed = JSON.parse(storedThumbnails);
        if (parsed && typeof parsed === 'object') {
          thumbnailSasCache.current = parsed;
          setResolvedThumbnailUrls(prev => ({ ...prev, ...parsed }));
        }
      }
    } catch (e) {
      console.warn('Failed to load thumbnail SAS cache from sessionStorage', e);
    }
  }, []);

  const handleAddModule = async () => {
    // Logic for handling add/update module
  };

  const handleEditModule = (module) => {
    // Logic for handling edit module
  };

  const handleAddQuestion = () => {
    // Logic for handling add question
  };

  const handleRemoveQuestion = (index) => {
    // Logic for handling remove question
  };

  const handleSubmitQuiz = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.BASEURL + `/api/learning_modules/${currentModule.videoid}/submit_quiz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ answers: quizAnswers }),
      });
      const result = await response.json();
      
      // Store result data and show results dialog
      setQuizResults({
        passed: result.passed,
        score: result.score || 0,
        // Prefer min passing score returned by the submit API, otherwise
        // fall back to the current module's `passingScore` field, then 70.
        minPassingScore: (result.minPassingScore ?? currentModule?.passingScore ?? 70),
      });
      setShowQuizResults(true);

      // Refresh modules list so UI reflects updated state immediately (but don't close dialogs yet)
      try {
        await fetchModules();
      } catch (err) {
        console.error('Error refreshing modules after quiz submission:', err);
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      setSnackbar({ open: true, message: t('learningmodules_quiz_error'), severity: 'error' });
    }
  };

  const getSasUrlForModule = async (module, forceRefresh = false) => {
    const THRESHOLD_MS = 2 * 60 * 1000; // refresh if less than 2 minutes left
    const id = String(module.videoid);
    const now = Date.now();
    const cached = sasCache.current[id];

    if (!forceRefresh && cached && cached.sas_url && cached.expires_at) {
      const expires = Date.parse(cached.expires_at);
      const startAt = cached.start_at ? Date.parse(cached.start_at) : 0;
      if (isNaN(expires)) {
        console.warn('Cached SAS has invalid expires_at', cached);
      } else {
        if (now < startAt) {
          console.warn('Cached SAS start time is in the future', { startAt, now, cached });
        } else if (expires - now > THRESHOLD_MS) {
          return cached.sas_url;
        }
      }
    }

    // fetch new SAS — use client-specific endpoint for custom modules, shared endpoint otherwise
    const isCustom = module.source === 'custom';
    const sasEndpoint = isCustom
      ? `${API_ENDPOINTS.BASEURL}/api/learning-modules/client-sas/fetch/${encodeURIComponent(id)}`
      : `${API_ENDPOINTS.BASEURL}/api/learning-module/sas/${encodeURIComponent(id)}`;
    const res = await fetch(sasEndpoint, { credentials: 'include' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to fetch SAS');
    }
    const json = await res.json();
    if (json && json.sas_url) {
      // accept multiple possible timestamp fields from backend
      const expires_at = json.expires_at || json.expiresAt || json.expiry || json.expire || json.expires;
      const start_at = json.start || json.start_at || json.startTime || json.startTimeUTC || null;

      sasCache.current[id] = { sas_url: json.sas_url, expires_at, start_at };
      // persist to sessionStorage
      try {
        const merged = { ...sasCache.current };
        sessionStorage.setItem('learning_sas_cache', JSON.stringify(merged));
      } catch (e) {
        console.warn('Failed to persist SAS cache to sessionStorage', e);
      }

      // basic validation
      const startMs = start_at ? Date.parse(start_at) : 0;
      const expiresMs = expires_at ? Date.parse(expires_at) : NaN;
      if (start_at && now < startMs) {
        console.warn('Received SAS with start time in the future', { start_at, now });
        // still return it, but caller may want to handle
      }
      if (!isNaN(expiresMs) && now >= expiresMs) {
        console.warn('Received SAS that is already expired', { expires_at, now });
        throw new Error('Received expired SAS from server');
      }

      return json.sas_url;
    }
    throw new Error('Invalid SAS response');
  };

  const getDisplayThumbnailUrl = (module) => {
    if (!module?.thumbnailURL) return null;
    if (module.source === 'custom') {
      const vid = String(module.videoid);
      if (resolvedThumbnailUrls[vid]) return resolvedThumbnailUrls[vid];
      if (thumbnailSasCache.current[vid]) return thumbnailSasCache.current[vid];
      return null;
    }
    return module.thumbnailURL;
  };

  const getThumbnailUrl = async (module) => {
    if (!module?.thumbnailURL) return null;
    // Shared modules have public SAS-embedded URLs; custom modules need a SAS fetch
    if (module.source !== 'custom') return module.thumbnailURL;
    const vid = String(module.videoid);
    if (thumbnailSasCache.current[vid]) return thumbnailSasCache.current[vid];
    try {
      const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/learning-modules/client-sas/thumbnail-sas/${encodeURIComponent(vid)}`, { credentials: 'include' });
      if (!res.ok) return module.thumbnailURL; // fallback
      const json = await res.json();
      if (json?.sas_url) {
        thumbnailSasCache.current[vid] = json.sas_url;
        return json.sas_url;
      }
    } catch (e) {
      console.warn('Failed to fetch thumbnail SAS', e);
    }
    return module.thumbnailURL;
  };

  const openModule = (module) => {
    setQuizAnswers({});
    setCurrentModule(module);
    setVideoSrc('');
    setVideoRefreshAttempts(0);
    setVideoDialogOpen(true);

    if (module?.videoType === 'AzureStorage' && module?.videoid) {
      setVideoLoading(true);
      getSasUrlForModule(module)
        .then((sas) => setVideoSrc(sas))
        .catch((err) => {
          console.error('Error fetching SAS:', err);
          setSnackbar({ open: true, message: t('learningmodules_no_video_error'), severity: 'error' });
        })
        .finally(() => setVideoLoading(false));
    }
  };

  const startQuiz = () => {
    setShowQuiz(true);
    setVideoDialogOpen(false);
    setCurrentQuestionIndex(0);
    setSelectedOption(null);
    setLastSelectionCorrect(null);
    setLastSelectionExplanation('');
  };

  const handleVideoError = async () => {
    if (!currentModule) return;
    if (videoRefreshAttempts >= 2) {
      setSnackbar({ open: true, message: t('learningmodules_no_video_error'), severity: 'error' });
      return;
    }

    try {
      setVideoLoading(true);
      const sas = await getSasUrlForModule(currentModule, true);
      setVideoSrc(sas);
      setVideoRefreshAttempts(prev => prev + 1);
    } catch (err) {
      console.error('Error refreshing SAS after video error:', err);
      setSnackbar({ open: true, message: t('learningmodules_no_video_error'), severity: 'error' });
    } finally {
      setVideoLoading(false);
    }
  };

  const allQuestionsAnswered = () => {
    if (!currentModule || !currentModule.questions) return true;
    return currentModule.questions.every(q => !!quizAnswers[q.question]);
  };

  const handleOptionSelect = async (q, rawOption, questionIndex) => {
    // rawOption may be string or object { option, optionAnswerExplanation }
    const optionText = typeof rawOption === 'string' ? rawOption : rawOption.option;

    const explanation = typeof rawOption === 'string' ? '' : (rawOption.optionAnswerExplanation || '');

    // Save answer for this question
    setQuizAnswers(prev => ({ ...prev, [q.question]: optionText }));
    setSelectedOption(optionText);

    // Call API to check answer (secure server-side validation)
    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASEURL}/api/learning_modules/${currentModule.videoid}/check_answer`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            questionIndex: questionIndex,
            selectedAnswer: optionText
          })
        }
      );

      if (!response.ok) {
        console.error('Error checking answer:', response.status);
        setSnackbar({ open: true, message: t('learningmodules_quiz_error'), severity: 'error' });
        return;
      }

      const result = await response.json();
      setLastSelectionCorrect(result.correct);
      // always store explanation if provided from API
      setLastSelectionExplanation(result.explanation || '');
    } catch (err) {
      console.error('Error calling check_answer API:', err);
      setSnackbar({ open: true, message: t('learningmodules_quiz_error'), severity: 'error' });
    }
  };

  const handleNext = () => {
    const next = currentQuestionIndex + 1;
    if (next < (currentModule?.questions?.length || 0)) {
      setCurrentQuestionIndex(next);
      setSelectedOption(null);
      setLastSelectionCorrect(null);
      setLastSelectionExplanation('');
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCloseQuizResults = () => {
    setShowQuizResults(false);
    setShowQuiz(false);
    setVideoDialogOpen(false);
    // Reset quiz state
    setQuizAnswers({});
    setCurrentQuestionIndex(0);
    setSelectedOption(null);
    setLastSelectionCorrect(null);
    setLastSelectionExplanation('');
  };

  const handleRetakeQuiz = () => {
    // Close results dialog
    setShowQuizResults(false);
    // Reset quiz state
    setQuizAnswers({});
    setCurrentQuestionIndex(0);
    setSelectedOption(null);
    setLastSelectionCorrect(null);
    setLastSelectionExplanation('');
    // Go back to video dialog
    setShowQuiz(false);
    setVideoDialogOpen(true);
  };

  const pendingModules = learningModules.filter(m => m.state === 'assigned' || (m.state === 'taken' && !m.passed));
  const passedModules = learningModules.filter(m => m.passed);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 } }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: { xs: 3, sm: 4 } }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t('learningmodules_title') || 'Learning Modules'}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640 }}>
            {t('learningmodules_subtitle') || 'Complete assigned learning modules and pass quizzes to advance.'}
          </Typography>
        </Box>
        {['admin', 'superadmin'].includes(role) && (
          <Button variant="contained" onClick={() => setShowAddModuleModal(true)} startIcon={<Replay />} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }}>
            {t('learningmodules_add_module')}
          </Button>
        )}
      </Box>

      {/* Pending - collapsible */}
      <Accordion defaultExpanded TransitionProps={{ timeout: 0 }} sx={{ mt: 3, borderRadius: 2, border: 1, borderColor: 'divider', '&::before': { display: 'none' }, boxShadow: 'none' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('learningmodules_pending_section') || 'Pending & Failed'} ({pendingModules.length})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {pendingModules.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('learningmodules_no_pending_modules') || 'No pending learning modules.'}</Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {pendingModules.map((module) => {
          const isAssigned = module.state === 'assigned';
          const isTaken = module.state === 'taken';
          const passed = module.passed;
          const score = module.score ?? 0;
          const statusLabel = isAssigned ? t('learningmodules_status_pending') : isTaken && passed ? t('learningmodules_status_passed') : t('learningmodules_status_failed');
          const statusColor = isAssigned ? 'warning' : isTaken && passed ? 'success' : 'error';

          return (
            <Grid item xs={12} sm={6} md={4} key={module._id}>
              <Card sx={{ height: '100%', borderRadius: 2, border: 1, borderColor: 'divider', boxShadow: 'none', display: 'flex', flexDirection: 'column', transition: 'all 0.15s', '&:hover': { borderColor: 'primary.light', boxShadow: 1 } }}>
                {getDisplayThumbnailUrl(module) ? (
                  <CardMedia
                    component="img"
                    image={getDisplayThumbnailUrl(module)}
                    height="160"
                    sx={{ objectFit: 'contain', bgcolor: 'grey.100' }}
                  />
                ) : (
                  <Box sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                    <Avatar sx={{ width: 80, height: 80, bgcolor: 'grey.300' }}>{module.title ? module.title[0] : '?'}</Avatar>
                  </Box>
                )}

                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {module.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {module.description ?? ''}
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 'auto' }}>
                    <Box sx={{ mb: 1 }}>
                      <Chip
                        label={statusLabel}
                        size="small"
                        color={statusColor}
                        icon={isAssigned ? <HourglassEmpty /> : passed ? <CheckCircle /> : <Replay />}
                        sx={{ px: 1, height: 28, fontSize: '0.75rem', ml: 0 }}
                      />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="caption" color="text.secondary">{t('total_score') || 'Progress'}</Typography>
                    <LinearProgress variant="determinate" value={score} sx={{ height: 8, borderRadius: 2, mt: 1 }} />
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">{score}%</Typography>
                      {isTaken && <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('learningmodules_score_label')} {score}/100</Typography>}
                    </Stack>
                  </Box>
                </CardContent>

                <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                  <Button fullWidth variant="contained" startIcon={<PlayArrow />} onClick={() => {
                    if (module.videoURL) openModule(module); else alert(t('learningmodules_no_video_error'));
                  }} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }}>
                    {isAssigned ? t('learningmodules_button_start') : passed ? t('learningmodules_button_review') : t('learningmodules_button_retake')}
                  </Button>
                </Box>
              </Card>
            </Grid>
          );
        })}
          </Grid>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Passed - collapsible */}
      {learningModules.some(m => m.passed) && (
        <Accordion TransitionProps={{ timeout: 0 }} sx={{ mt: 3, borderRadius: 2, border: 1, borderColor: 'divider', '&::before': { display: 'none' }, boxShadow: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('learningmodules_passed_section') || 'Passed Modules'} ({learningModules.filter(m => m.passed).length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {learningModules.filter(m => m.passed).map((module) => (
                <Grid item xs={12} sm={6} md={4} key={module._id}>
                  <Card sx={{ height: '100%', borderRadius: 2, border: 1, borderColor: 'divider', boxShadow: 'none', display: 'flex', flexDirection: 'column', transition: 'all 0.15s', '&:hover': { borderColor: 'primary.light', boxShadow: 1 } }}>
                    {getDisplayThumbnailUrl(module) ? (
                      <CardMedia
                        component="img"
                        image={getDisplayThumbnailUrl(module)}
                        height="160"
                        sx={{ objectFit: 'contain', bgcolor: 'grey.100' }}
                      />
                    ) : (
                      <Box sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                        <Avatar sx={{ width: 80, height: 80, bgcolor: 'grey.300' }}>{module.title ? module.title[0] : '?'}</Avatar>
                      </Box>
                    )}

                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            mb: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {module.title}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {module.description ?? ''}
                        </Typography>
                      </Box>

                      <Box sx={{ mt: 'auto' }}>
                        <Box sx={{ mb: 1 }}>
                          <Chip
                            label={t('learningmodules_status_passed') || 'Passed'}
                            size="small"
                            color="success"
                            icon={<CheckCircle />}
                            sx={{ px: 1, height: 28, fontSize: '0.75rem', ml: 0 }}
                          />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="caption" color="text.secondary">{t('total_score') || 'Progress'}</Typography>
                        <LinearProgress variant="determinate" value={module.score ?? 100} sx={{ height: 8, borderRadius: 2, mt: 1 }} />
                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">{module.score ?? 100}%</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('learningmodules_score_label')} {module.score ?? 100}/100</Typography>
                        </Stack>
                      </Box>
                    </CardContent>

                    <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                      <Button fullWidth variant="contained" startIcon={<PlayArrow />} onClick={() => {
                        if (module.videoURL) openModule(module); else alert(t('learningmodules_no_video_error'));
                      }} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }}>
                        {t('learningmodules_button_review') || 'Review'}
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Video Dialog */}
      <Dialog open={videoDialogOpen} onClose={() => setVideoDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{currentModule?.title}</span>
          <IconButton onClick={() => setVideoDialogOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {currentModule?.videoType === 'YouTube' ? (
            <Box sx={{ position: 'relative', pt: '56.25%' }}>
              <iframe
                src={currentModule.videoURL}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Learning Module Video"
              />
            </Box>
          ) : currentModule?.videoType === 'AzureStorage' ? (
            videoLoading ? (
              <Box sx={{ width: '100%', textAlign: 'center', py: 6 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>{t('learningmodules_video_loading') || 'Loading video...'}</Typography>
              </Box>
            ) : videoSrc ? (
              <video
                src={videoSrc}
                poster={getDisplayThumbnailUrl(currentModule) || undefined}
                style={{ width: '100%', borderRadius: 8 }}
                controls
                controlsList="nodownload"
                onContextMenu={e => e.preventDefault()}
                onError={handleVideoError}
                onLoadedData={() => setVideoRefreshAttempts(0)}
              />
            ) : (
              // If no SAS/video URL but a thumbnail is available, show the thumbnail as a placeholder
              getDisplayThumbnailUrl(currentModule) ? (
                <Box sx={{ width: '100%', textAlign: 'center' }}>
                  <img src={getDisplayThumbnailUrl(currentModule)} alt={currentModule.title || 'Module thumbnail'} style={{ width: '100%', borderRadius: 8 }} />
                  <Typography sx={{ mt: 1 }} color="text.secondary">{t('learningmodules_no_video_error') || 'Video unavailable'}</Typography>
                </Box>
              ) : (
                <Typography color="error">{t('learningmodules_no_video_error') || 'Video unavailable'}</Typography>
              )
            )
          ) : (
            <Typography>{t('learningmodules_no_video')}</Typography>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('learningmodules_instructions')}</Typography>
            <Typography variant="body2" color="text.secondary">{currentModule?.description}</Typography>

            <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: 'background.paper' }}>
              <Typography variant="body2" color="text.secondary">
                {t('learningmodules_watch_and_quiz_instructions')}
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setVideoDialogOpen(false)} sx={{ textTransform: 'none' }}>{t('cancel') || 'Close'}</Button>
          <Button variant="contained" startIcon={<QuizIcon />} onClick={startQuiz} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>
            {t('learningmodules_button_continue_quiz')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quiz Dialog */}
      <Dialog open={showQuiz} onClose={() => setShowQuiz(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>{t('learningmodules_quiz_title')} {currentModule?.title}<IconButton onClick={() => setShowQuiz(false)}><Close /></IconButton></DialogTitle>
        <DialogContent dividers>
          {currentModule?.questions && currentModule.questions.length > 0 ? (
            (() => {
              const q = currentModule.questions[currentQuestionIndex];
              if (!q) return <Typography>{t('learningmodules_no_questions')}</Typography>;
              const optionsArray = q.options || q.answers || [];

              return (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontWeight: 700 }}>{currentQuestionIndex + 1}. {q.question}</Typography>

                  <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
                    {optionsArray.map((opt, i) => {
                      const optText = typeof opt === 'string' ? opt : opt.option;
                      const isSelected = selectedOption === optText;

                      // style selected option: green for correct (from API), red for wrong
                      const selectedStyles = isSelected ? {
                        bgcolor: lastSelectionCorrect ? 'success.main' : 'error.main',
                        color: 'common.white',
                        '&:hover': { bgcolor: lastSelectionCorrect ? 'success.dark' : 'error.dark' }
                      } : {};

                      return (
                        <Button
                          key={i}
                          fullWidth
                          variant={isSelected ? 'contained' : 'outlined'}
                          onClick={() => { if (!selectedOption) handleOptionSelect(q, opt, currentQuestionIndex); }}
                          disabled={!!selectedOption && !isSelected}
                          sx={{ justifyContent: 'flex-start', textTransform: 'none', ...selectedStyles }}
                        >
                          {optText}
                        </Button>
                      );
                    })}
                  </Box>

                  {lastSelectionExplanation && (
                    <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: 'background.paper' }}>
                      <Typography sx={{ color: lastSelectionCorrect ? 'text.primary' : 'error.main' }} variant="body2">{lastSelectionExplanation}</Typography>
                    </Paper>
                  )}
                </Box>
              );
            })()
          ) : (
            <Typography>{t('learningmodules_no_questions')}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setShowQuiz(false)} sx={{ textTransform: 'none' }}>{t('cancel') || 'Cancel'}</Button>
          {currentModule?.questions && currentQuestionIndex < (currentModule.questions.length - 1) ? (
            <Button disabled={!selectedOption} variant="contained" onClick={handleNext} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>
              {t('next') || 'Next'}
            </Button>
          ) : (
            <Button disabled={!allQuestionsAnswered()} variant="contained" onClick={handleSubmitQuiz} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>
              {t('learningmodules_button_submit_quiz')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Quiz Results Dialog */}
      <Dialog open={showQuizResults} onClose={handleCloseQuizResults} maxWidth="sm" fullWidth disableEscapeKeyDown={true}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <span>{t('results') }</span>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            {/* Large Score Display */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h2" sx={{ fontWeight: 700, color: quizResults.passed ? 'success.main' : 'error.main' }}>
                {quizResults.score}%
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
                {t('total_score') || 'Your Score'}
              </Typography>
            </Box>

            {/* Passed Message */}
            {quizResults.passed ? (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  {t('learningmodules_passed_congratulations') || '🎉 Congratulations!'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('learningmodules_quiz_passed_message') || 'You have successfully completed this module. Great job!'}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'error.main' }}>
                  {t('failed') || 'Module Not Completed'}
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 2,
                    p: 2,
                    // use neutral background for contrast and a subtle warning border
                    bgcolor: 'background.paper',
                    borderColor: 'warning.main',
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    <strong>{t('learningmodules_minimum_passing_score') || 'Minimum Passing Score'}:</strong> {quizResults.minPassingScore}%
                  </Typography>

                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                    {t('learningmodules_quiz_failed') || 'Please review the module and try again.'}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleCloseQuizResults} variant={quizResults.passed ? 'contained' : 'outlined'} sx={{ textTransform: 'none' }}>
            {t('close') || 'Close'}
          </Button>
          {!quizResults.passed && (
            <Button onClick={handleRetakeQuiz} variant="contained" startIcon={<Replay />} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>
              {t('learningmodules_button_retake') || 'Retake Module'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <FixedSnackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}

export default LearningModules;
